import {
  Headphones,
  Building2,
  Wrench,
  Radio,
  Phone,
  Tv,
  Dumbbell,
  Heart,
  GraduationCap,
  Home,
  Cpu,
  Stethoscope,
  Mic,
  Gamepad2,
  Monitor,
  Scale,
  Trophy,
  Rocket,
  Disc3,
  Palette,
  Sparkles,
  ListMusic,
  type LucideIcon,
} from "lucide-react";

export type Category = "All" | "Industry Analysis" | "Developer Guide" | "Case Study" | "Technical" | "Comparison";

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  icon: LucideIcon;
  category: Category;
  readTime: string;
  date: string;
  tags: string[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "mcp-music-primitive",
    title: "StarSinger: The Audio Primitive Every AI Agent Needs",
    excerpt:
      "MCP passed 97M monthly SDK downloads and 10,000+ public servers by December 2025, yet music is still thin in the agent stack. StarSinger is building the audio primitive.",
    icon: Cpu,
    category: "Industry Analysis",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["MCP", "A2A Protocol", "AI Agents", "Infrastructure"],
  },
  {
    slug: "commercial-music-revolution",
    title: "How AI Agents Are Changing Commercial Background Music",
    excerpt:
      "Background music for businesses is moving from fixed playlists to AI-curated, context-aware audio. What that means for venues — and what still needs licensing.",
    icon: Headphones,
    category: "Industry Analysis",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Muzak", "Mood Media", "Commercial Music"],
  },
  {
    slug: "internet-radio-zero-royalties",
    title: "Build a 24/7 AI Radio Station with StarSinger MCP",
    excerpt:
      "Internet radio pays per-performance webcasting royalties. Here is how to run an always-on AI station with StarSinger MCP and AzuraCast — and what the streaming license does and doesn't cover.",
    icon: Radio,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Internet Radio", "AzuraCast", "College Radio", "Royalties"],
  },
  {
    slug: "twitch-dmca-safe-music",
    title: "Original AI Background Music for Twitch and YouTube Streamers",
    excerpt:
      "Copyright takedowns keep hitting streamers. StarSinger's catalog is original AI music owned by StarSinger — here is how to use it on stream, and what the license covers.",
    icon: Tv,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Twitch", "YouTube", "DMCA", "Content ID"],
  },
  {
    slug: "hold-music-mcp",
    title: "Replace Your $660/Year Hold Music Subscription with StarSinger MCP",
    excerpt:
      "Snap Recordings charges $35-55/month. Every business with a phone system pays for hold music. An MCP agent generates fresh, on-brand audio dynamically.",
    icon: Phone,
    category: "Developer Guide",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["Hold Music", "VoIP", "Business Phone", "SaaS"],
  },
  {
    slug: "fitness-class-music",
    title: "AI Workout Playlists: BPM-Matched Music for Fitness Classes",
    excerpt:
      "Gyms face real music-licensing obligations. StarSinger MCP builds BPM-matched workout playlists from an owned AI catalog — here is how it works and what to check.",
    icon: Dumbbell,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Fitness", "Peloton", "BPM", "Gym"],
  },
  {
    slug: "sleep-meditation-apps",
    title: "Building a Calm/Headspace Competitor with StarSinger MCP",
    excerpt:
      "Calm has passed 180M downloads, and meditation apps are a growing market. Here is how developers can build one on AI-generated calming audio.",
    icon: Heart,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Calm", "Headspace", "Meditation", "Wellness"],
  },
  {
    slug: "smart-home-music-agent",
    title: "Connect StarSinger MCP to Your Smart Home Agent",
    excerpt:
      "Smart speakers are in hundreds of millions of homes. OpenClaw controls Sonos via CLI. StarSinger MCP adds mood-aware music to your 'good morning' routine and party mode.",
    icon: Home,
    category: "Developer Guide",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["Smart Home", "IoT", "Sonos", "OpenClaw"],
  },
  {
    slug: "indie-game-adaptive-music",
    title: "Adaptive Game Soundtracks with StarSinger MCP",
    excerpt:
      "An original adaptive soundtrack is a real cost for an indie game. StarSinger MCP lets an indie game pick catalog music by game state — here is how, and what the license covers.",
    icon: Gamepad2,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Game Dev", "FMOD", "Unity", "Godot"],
  },
  {
    slug: "podcast-background-music",
    title: "Automate Podcast Music Selection with StarSinger MCP",
    excerpt:
      "Podcasts need background music. An MCP agent can pick tracks from StarSinger's catalog by episode mood in one query — check the license terms for your show.",
    icon: Mic,
    category: "Developer Guide",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["Podcasts", "Descript", "Riverside", "Audio"],
  },
  {
    slug: "elearning-course-soundtracks",
    title: "Auto-Soundtrack Your Online Courses with StarSinger MCP",
    excerpt:
      "Online courses need background music, and picking it by hand is slow. An AI agent using MCP can auto-select mood-matched tracks per lesson.",
    icon: GraduationCap,
    category: "Developer Guide",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["E-Learning", "EdTech", "LMS", "Courses"],
  },
  {
    slug: "medical-waiting-room-music",
    title: "AI-Curated Music for Dental and Medical Offices via StarSinger MCP",
    excerpt:
      "The US has about 135,000 dental practices, and waiting rooms need calming music. An AI receptionist agent manages audio based on appointments, demographics, and procedure type.",
    icon: Stethoscope,
    category: "Developer Guide",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["Healthcare", "Dental", "Medical", "Waiting Room"],
  },
  {
    slug: "digital-signage-audio-sync",
    title: "Sync Digital Signage Audio with AI in Real Time Using StarSinger MCP",
    excerpt:
      "Digital signage keeps audio and video in separate silos. An MCP agent watches content feeds and matches music to visuals in real time.",
    icon: Monitor,
    category: "Technical",
    readTime: "7 min read",
    date: "March 2026",
    tags: ["Digital Signage", "Raydiant", "Retail Tech"],
  },
  {
    slug: "mcp-for-av-integrators",
    title: "MCP for AV Integrators: Adding AI Music to Your Service Stack",
    excerpt:
      "A technical guide for commercial audio installers. Deploy AI-curated music systems that adapt to weather, occupancy, and time of day.",
    icon: Wrench,
    category: "Technical",
    readTime: "12 min read",
    date: "March 2026",
    tags: ["AV Integrators", "System Design", "API"],
  },
  {
    slug: "starsinger-vs-mood-media",
    title: "StarSinger MCP vs Mood Media: AI-Generated vs Licensed Business Music",
    excerpt:
      "Mood Media serves 500,000+ customer locations, from $17-35/mo per location with licensing included. StarSinger MCP offers an owned AI-generated catalog through an API. A fair comparison of both approaches.",
    icon: Scale,
    category: "Comparison",
    readTime: "9 min read",
    date: "March 2026",
    tags: ["Mood Media", "Muzak", "Comparison", "Pricing"],
  },
  {
    slug: "starsinger-vs-soundmachine",
    title: "StarSinger MCP vs SoundMachine: Which Business Music Platform in 2026?",
    excerpt:
      "SoundMachine offers 61M licensed tracks with licensing bundled in the US and Canada. StarSinger MCP offers AI-generated music with MCP integration. Which fits your business?",
    icon: Scale,
    category: "Comparison",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["SoundMachine", "Comparison", "Business Music"],
  },
  {
    slug: "starsinger-vs-epidemic-sound",
    title: "StarSinger MCP vs Epidemic Sound: Music for Creators Compared",
    excerpt:
      "Epidemic Sound clears its music for creators' channels and has its own MCP connector. StarSinger offers an owned AI catalog through an API. Which is right for your content?",
    icon: Scale,
    category: "Comparison",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Epidemic Sound", "Creators", "DMCA", "Comparison"],
  },
  {
    slug: "starsinger-vs-spotify-business",
    title: "StarSinger MCP vs Spotify for Business: Why Consumer Streaming Fails Commercially",
    excerpt:
      "Can you use Spotify in your store? No -- it violates their ToS. Here's how business music services compare to StarSinger MCP for commercial use.",
    icon: Scale,
    category: "Comparison",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Spotify", "Commercial Music", "Licensing", "Comparison"],
  },
  {
    slug: "starsinger-vs-mubert",
    title: "StarSinger MCP vs Mubert: Two Approaches to AI Music for Developers",
    excerpt:
      "Mubert generates music in real-time. StarSinger curates a searchable catalog via MCP. Two different architectures for AI music -- here's how they compare.",
    icon: Scale,
    category: "Comparison",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Mubert", "AI Music", "API", "Comparison"],
  },
  {
    slug: "best-music-for-restaurants",
    title: "Best Background Music Services for Restaurants in 2026",
    excerpt:
      "Comparing Mood Media, SoundMachine, Rockbot, StarSinger MCP, and CloudCover for restaurant audio. Which service fits your restaurant type?",
    icon: Trophy,
    category: "Comparison",
    readTime: "10 min read",
    date: "March 2026",
    tags: ["Restaurants", "Best Of", "Roundup", "Commercial Music"],
  },
  {
    slug: "best-music-for-hotels",
    title: "Best Music Solutions for Hotels and Hospitality in 2026",
    excerpt:
      "From luxury chains to boutique properties: comparing 5 music providers across lobby, spa, gym, restaurant, and bar zones for hospitality.",
    icon: Trophy,
    category: "Comparison",
    readTime: "10 min read",
    date: "March 2026",
    tags: ["Hotels", "Hospitality", "Best Of", "Roundup"],
  },
  {
    slug: "best-music-for-retail",
    title: "Best In-Store Music for Retail Chains in 2026",
    excerpt:
      "Fashion, grocery, electronics, luxury -- different retail formats need different music solutions. Comparing 5 providers for in-store audio.",
    icon: Trophy,
    category: "Comparison",
    readTime: "10 min read",
    date: "March 2026",
    tags: ["Retail", "In-Store", "Best Of", "Roundup"],
  },
  {
    slug: "10-startups-this-weekend",
    title: "10 Startups You Can Build This Weekend with StarSinger MCP",
    excerpt:
      "From AI DJ agents to mood-matched dating apps -- 10 indie-scale startup ideas with technical sketches you can prototype in a weekend hackathon.",
    icon: Rocket,
    category: "Developer Guide",
    readTime: "12 min read",
    date: "March 2026",
    tags: ["Startups", "Hackathon", "Side Projects", "Ideas"],
  },
  {
    slug: "ai-dj-agent",
    title: "Build an AI DJ Agent That Reads the Room",
    excerpt:
      "Feed your agent time of day, weather, and crowd energy. It searches by mood, builds playlists, and transitions between tracks. Full Playlist API walkthrough.",
    icon: Disc3,
    category: "Developer Guide",
    readTime: "10 min read",
    date: "March 2026",
    tags: ["DJ", "Playlist API", "AI Agent", "Events"],
  },
  {
    slug: "ai-mixtapes-50-lines",
    title: "AI Mixtapes: Build a Shareable Music Experience in 50 Lines of Code",
    excerpt:
      "Users describe a vibe. Your app searches, curates, and creates a public playlist they can share. Tutorial using search + create_playlist + public playlists.",
    icon: Palette,
    category: "Developer Guide",
    readTime: "8 min read",
    date: "March 2026",
    tags: ["Mixtapes", "Playlist API", "Tutorial", "Shareable"],
  },
  {
    slug: "music-as-a-feature",
    title: "Music as a Feature: How 5 App Categories Can Add Audio with One API",
    excerpt:
      "Dating apps, study tools, therapy platforms, games, and fitness trackers -- how adding a music layer transforms user engagement and retention.",
    icon: Sparkles,
    category: "Industry Analysis",
    readTime: "9 min read",
    date: "March 2026",
    tags: ["Product", "Engagement", "Dating", "Wellness", "Gaming"],
  },
  {
    slug: "playlist-api-deep-dive",
    title: "The StarSinger Playlist API: Build Spotify-Like Curation for Your App",
    excerpt:
      "Deep dive into the new Playlist API: create, list, update, reorder, and share playlists. Tier limits, MCP tools, and REST endpoints explained.",
    icon: ListMusic,
    category: "Technical",
    readTime: "10 min read",
    date: "March 2026",
    tags: ["Playlist API", "REST", "MCP Tools", "Curation"],
  },
];

export const categories: Category[] = ["All", "Industry Analysis", "Developer Guide", "Comparison", "Technical"];

export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

export function getAdjacentPosts(slug: string): { prev: BlogPost | null; next: BlogPost | null } {
  const idx = blogPosts.findIndex((p) => p.slug === slug);
  return {
    prev: idx > 0 ? blogPosts[idx - 1] : null,
    next: idx < blogPosts.length - 1 ? blogPosts[idx + 1] : null,
  };
}
